import { version as shellReactVersion } from 'react';
import { ModuleFederationRuntimePlugin as FederationRuntimePlugin } from '@module-federation/enhanced/runtime';

/**
 * Backwards compatibility for remotes built against an older React major than the shell.
 *
 * Every shared dependency is a singleton, and a singleton resolves to the highest version
 * registered. With the shell providing React 19, router 7, styled-components 6 and UDS 5,
 * a React 18 remote would be handed that whole stack and crash — most visibly in its own
 * bundled React 18 JSX runtime, which reads React internals that 19 renamed.
 *
 * Instead, legacy remotes are initialised against a separate share scope. Each one registers
 * its own fallback copies there (React 18, router 6, ...) and they share those with one
 * another, so a legacy page and the legacy ccoe-common provider still agree on one React.
 * The shell then mounts them in a React 18 root of their own (LegacyReactIsland).
 */

type SharedEntry = {
  version: string;
  loaded?: boolean;
  loading?: Promise<unknown>;
  lib?: () => unknown;
  get: () => Promise<() => unknown>;
};
type SharedVersions = Record<string, SharedEntry>;
type ShareScope = Record<string, SharedVersions>;
type ShareScopeMap = Record<string, ShareScope>;

const DEFAULT_SCOPE = 'default';

const isInUse = (entry: SharedEntry | undefined): boolean =>
  Boolean(entry && (entry.loaded || entry.loading || entry.lib));

const guardedVersions = new WeakSet<object>();

/**
 * When a remote registers a version another remote already registered, the runtime swaps
 * in its own copy unless the first one is `loaded` — even while that copy is still loading.
 * Two legacy remotes initialising together (a CCoE page and ccoe-common) then each end up
 * with their own React, and whichever router wins the singleton calls hooks on a React that
 * isn't rendering. Once a copy is in use, it stays the registered one.
 */
const keepInUseVersions = (versions: SharedVersions): SharedVersions => {
  if (guardedVersions.has(versions)) return versions;
  const guarded = new Proxy(versions, {
    set(target, version: string, entry: SharedEntry) {
      if (!isInUse(target[version])) target[version] = entry;
      return true;
    },
  });
  guardedVersions.add(guarded);
  return guarded;
};

const createLegacyShareScope = (): ShareScope =>
  new Proxy({} as ShareScope, {
    set(target, pkgName: string, versions: SharedVersions) {
      target[pkgName] = keepInUseVersions(versions);
      return true;
    },
  });

const majorOf = (version: string | undefined): number => Number.parseInt(version ?? '', 10);

const SHELL_REACT_MAJOR = majorOf(shellReactVersion);

const legacyRemotes = new Set<string>();
const legacyShareScopeMap: ShareScopeMap = {};

export const isLegacyReactRemote = (remoteName: string): boolean => legacyRemotes.has(remoteName);

export const requiresLegacyReact = (sharedReactVersion: string | undefined): boolean =>
  majorOf(sharedReactVersion) < SHELL_REACT_MAJOR;

/**
 * Returns the copy of a package the legacy remotes resolved, or undefined when no legacy
 * remote shares it. Singleton resolution prefers an already-loaded version, so we do too.
 */
export const loadLegacyShare = async <T>(pkgName: string): Promise<T | undefined> => {
  const versions = Object.values(legacyShareScopeMap[DEFAULT_SCOPE]?.[pkgName] ?? {});
  const entry = versions.find(v => v.loaded || v.lib) ?? versions[0];
  if (!entry) return undefined;
  const lib = entry.lib ?? (await entry.get());
  return lib() as T;
};

export const legacyReactPlugin: () => FederationRuntimePlugin = () => ({
  name: 'legacy-react-plugin',

  async afterLoadSnapshot(args) {
    const react = args.remoteSnapshot?.shared?.find(s => s.sharedName === 'react');
    if (react && requiresLegacyReact(react.version)) {
      legacyRemotes.add(args.moduleInfo.name);
    }
    return args;
  },

  async beforeInitContainer(args) {
    if (!legacyRemotes.has(args.remoteInfo.name)) return args;

    const scopeKeys = ([] as string[]).concat(args.remoteEntryInitOptions.shareScopeKeys);
    scopeKeys.forEach(key => {
      legacyShareScopeMap[key] ??= createLegacyShareScope();
    });

    const remoteEntryInitOptions = { ...args.remoteEntryInitOptions };
    // The runtime reads the host map from this non-enumerable property, so it must be set
    // the same way to keep the remote from falling back to the shell's scope.
    Object.defineProperty(remoteEntryInitOptions, 'shareScopeMap', {
      value: legacyShareScopeMap,
      enumerable: false,
    });

    return {
      ...args,
      shareScope: legacyShareScopeMap[scopeKeys[0]] as any,
      remoteEntryInitOptions,
    };
  },
});
